import { ChevronLeft, ChevronRight } from "lucide-react";

import { showValue } from "@/lib/integrations/settings";

import { Button } from "./controls";
import styles from "./integrations.module.css";

/** A nested value as `path → value` lines, lists shown whole. */
export function flatten(value: unknown, prefix = "", out: [string, unknown][] = [], depth = 0): [string, unknown][] {
  if (value !== null && typeof value === "object" && !Array.isArray(value) && depth < 6) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0 && prefix) out.push([prefix, value]);
    for (const [key, child] of entries) flatten(child, prefix ? `${prefix}.${key}` : key, out, depth + 1);
    return out;
  }
  out.push([prefix || "(value)", value]);
  return out;
}

function Pairs({ value, empty }: { value: unknown; empty: string }) {
  const lines = value === null || value === undefined ? [] : flatten(value);
  if (lines.length === 0) return <p className={styles.faint}>{empty}</p>;
  return (
    <dl className={styles.pairs}>
      {lines.map(([path, one]) => (
        <div key={path} style={{ display: "contents" }}>
          <dt>{path}</dt>
          <dd>{showValue(one, 200)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Their row beside our mapped row, and a way to step through the run's rows. */
export function SampleRows({
  theirs,
  ours,
  index,
  total,
  onIndex,
  note,
}: {
  theirs: unknown;
  ours: unknown;
  /** Zero-based row shown. */
  index: number;
  total: number;
  onIndex?: (index: number) => void;
  note?: string;
}) {
  return (
    <div style={{ display: "grid", gap: "var(--s2)" }}>
      {onIndex && (
        <div className={styles.inline}>
          <Button tone="subtle" icon aria-label="Row before" disabled={index <= 0} onClick={() => onIndex(index - 1)}>
            <ChevronLeft size={15} />
          </Button>
          <span className={styles.muted}>
            Row {(index + 1).toLocaleString()} of {total.toLocaleString()}
          </span>
          <Button tone="subtle" icon aria-label="Row after" disabled={index >= total - 1} onClick={() => onIndex(index + 1)}>
            <ChevronRight size={15} />
          </Button>
          {note && <span className={styles.faint}>{note}</span>}
        </div>
      )}
      <div className={styles.grid2}>
        <div className={styles.box}>
          <div className={styles.muted} style={{ marginBottom: "var(--s2)", fontWeight: 600 }}>
            What Scout sent
          </div>
          <Pairs value={theirs} empty="Nothing." />
        </div>
        <div className={styles.box}>
          <div className={styles.muted} style={{ marginBottom: "var(--s2)", fontWeight: 600 }}>
            What omnium makes of it
          </div>
          <Pairs value={ours} empty="Nothing mapped for this row." />
        </div>
      </div>
    </div>
  );
}
